import { HomeContainerCard } from './HomeContainerCard';
import { getHomeContainers } from '@/api';
import { Device } from '@/classes';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { HomeContainer } from '@/types';
import { isFocusScrollSuppressed, scrollElementTo } from '@/utils';
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useEffect, useState } from 'react';

export const HomeContainerList = () => {
  const [loading, setLoading] = useState(false);
  const [homeContainers, setHomeContainers] = useState<Array<HomeContainer>>([]);
  // ! focusable: false — 컨테이너 자신은 절대 포커스 대상이 되지 않게 한다. (isContainer 주석 참고)
  const { ref, focusKey } = useExtendFocusable({
    isContainer: true,
    saveLastFocusedChild: true,
    trackChildren: true,
    autoRestoreFocus: true,
    focusable: false,
  });

  const loadHomeContainers = useCallback(async () => {
    try {
      setLoading(true);

      const homeContainers = await getHomeContainers({
        deviceType: 'TV',
        contentType: 'VIDEO',
        language: Device.language,
        thumbnailType: 'IMAGE',
        resolution: 'P33',
      });

      // ! Single Banner 제외
      setHomeContainers(
        homeContainers.filter((homeContainer) => {
          const isBanner = homeContainer.viewType === 'SINGLE_BANNER';
          const isAllGame = homeContainer.contents.every((content) => content.type === 'GAME');
          const isEmpty = homeContainer.contents.length === 0;

          return !isBanner && !isAllGame && !isEmpty;
        }),
      );
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load content groups', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const onFocused = useCallback(
    ({ top }: { top: number }) => {
      if (isFocusScrollSuppressed('vertical')) {
        return;
      }

      const current = ref.current;

      if (current) {
        scrollElementTo({ element: current, top: top - 200 });
      }
    },
    [ref],
  );

  useEffect(() => {
    loadHomeContainers();
  }, [loadHomeContainers]);

  return (
    <main
      id="main-container"
      className="w-full h-screen overflow-x-hidden overflow-y-auto bg-[#FFFDF1] pt-[36px]"
      ref={ref}
    >
      <FocusContext.Provider value={focusKey}>
        {loading && (
          <div className="flex justify-center items-center h-screen w-full">
            <div className="w-[80px] h-[80px] border-4 border-yellow-300 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {homeContainers.map((homeContainer, i) => (
          <HomeContainerCard
            sourcePage="home"
            key={homeContainer.id}
            index={i}
            homeContainer={homeContainer}
            onFocused={onFocused}
          />
        ))}
      </FocusContext.Provider>
    </main>
  );
};
